import React, { forwardRef, JSX } from 'react'
import './index.scss'
import { observer } from 'mobx-react'
import ModalPortal from '@Components/Popups/ModalPortal'
import UploadDocumentConfirmationPopup from '@Components/Popups/UploadDocumentConfirmationPopup'
import { Maybe } from '@Types'
import DocumentUploadFormButtons from '@Components/DocumentUploadForm/DocumentUploadFormButtons'
import { formatBytes } from '@App/helpers/format-bytes'
import FilesPreview from '@Components/DocumentUploadForm/FilesPreview'
import { useDocumentUpload } from '@App/services/useDocumentUpload'
import { PresignedDocumentUpload, UnifiedDocument, UploadedDocument } from '@Entities/document/document-to-upload'
import UploadSelectionSection from '@Components/DocumentUploadForm/UploadSelectionSection'
import DuplicateDocumentPopup from '@Components/Popups/ConfirmationPopup/DuplicateDocumentPopup'

export interface UploadDocumentsFormProps {
    title?: JSX.Element
    allowedFilesConfig: { allowedFileTypes: string[]; maximumFileSizeInBytes: number }
    generateS3PresignedUrl(
        filename: string,
        fileSize: number,
        description?: Maybe<string>,
        fileHash?: Maybe<string>
    ): Promise<PresignedDocumentUpload>
    confirmationPopupConfig?: { confirmationPopupTitle: string; confirmationPopupBody: React.JSX.Element }
    displayConfig: {
        descriptionHelpText?: string
        browseFilesButtonText?: string
        hideUploadButtons?: boolean
        hideHeader?: boolean
        hideConfirmationText?: boolean
        showDescription?: boolean
    }
    onFileStaged?: (files: UnifiedDocument[]) => void
    onDoneClicked?: () => void
    onUpload?: () => void | Promise<void>
    completedUploadConfig?: { uploadedDocuments: UploadedDocument[]; allowAdditionalUploads: boolean }
    allowMultipleUploads?: boolean
    showDescriptionUpdate?: boolean
    hashFiles?: boolean
}

export interface DocumentUploadFormButtonsOverride {
    uploadDocuments: () => void
    showUploadDisclaimer: () => void
    resetForm: () => void
}

const DocumentUploadForm = forwardRef<DocumentUploadFormButtonsOverride, UploadDocumentsFormProps>((props, ref) => {
    const {
        title,
        generateS3PresignedUrl,
        confirmationPopupConfig,
        onFileStaged,
        onDoneClicked,
        onUpload,
        allowMultipleUploads = true,
        allowedFilesConfig: { allowedFileTypes, maximumFileSizeInBytes },
        displayConfig: {
            descriptionHelpText,
            browseFilesButtonText = 'Browse files',
            hideUploadButtons,
            hideHeader,
            hideConfirmationText,
            showDescription = true,
        },
        completedUploadConfig,
        showDescriptionUpdate = true,
        hashFiles = false,
    } = props

    const {
        uploadedFiles,
        isUploading,
        uploadDocuments,
        resetForm,
        onFilesChosen,
        setDisplayUploadDisclaimer,
        displayUploadDisclaimer,
        filesWereUploaded,
        browseFilePickerRef,
        onRemove,
        onDescriptionChange,
        duplicateDocuments,
        onDuplicateDocumentsContinue,
    } = useDocumentUpload(
        generateS3PresignedUrl,
        allowedFileTypes,
        maximumFileSizeInBytes,
        ref,
        onFileStaged,
        onUpload,
        completedUploadConfig?.uploadedDocuments ?? [],
        hashFiles
    )

    const uploadFileTypesString = allowedFileTypes.join(', ')
    const maxFileSizeString = formatBytes(maximumFileSizeInBytes)
    const shouldShowUploadZone = !(completedUploadConfig && !completedUploadConfig.allowAdditionalUploads)

    const handleUploadClicked = async () => {
        if (confirmationPopupConfig) {
            setDisplayUploadDisclaimer(true)
        } else {
            await uploadDocuments()
        }
    }

    return (
        <div className="upload-documentation-form">
            {confirmationPopupConfig && (
                <ModalPortal isOpen={displayUploadDisclaimer}>
                    <UploadDocumentConfirmationPopup
                        onClose={() => setDisplayUploadDisclaimer(false)}
                        confirmationPopupTitle={confirmationPopupConfig.confirmationPopupTitle}
                        confirmationPopupBody={confirmationPopupConfig.confirmationPopupBody}
                        onConfirm={uploadDocuments}
                        isDisabled={isUploading}
                    />
                </ModalPortal>
            )}
            <ModalPortal isOpen={duplicateDocuments.length > 0}>
                <DuplicateDocumentPopup
                    duplicateDocuments={duplicateDocuments}
                    onContinue={onDuplicateDocumentsContinue}
                />
            </ModalPortal>
            {!hideHeader && (
                <>
                    {title}
                    {showDescription && (
                        <p>
                            {`Please select the file you want to upload. Your files should be in ${uploadFileTypesString} and not exceed ${maxFileSizeString} in size.`}
                        </p>
                    )}
                </>
            )}

            {shouldShowUploadZone && (
                <UploadSelectionSection
                    onFilesChosen={onFilesChosen}
                    maxFileSizeString={maxFileSizeString}
                    uploadFileTypesString={uploadFileTypesString}
                    allowMultipleUploads={allowMultipleUploads}
                    browseFilesButtonText={browseFilesButtonText}
                    browseFilePickerRef={browseFilePickerRef}
                />
            )}
            <FilesPreview
                uploadedFiles={uploadedFiles}
                filesWereUploaded={filesWereUploaded}
                onRemove={onRemove}
                onDescriptionChange={showDescriptionUpdate ? onDescriptionChange : undefined}
                descriptionHelpText={descriptionHelpText}
                hideConfirmationText={hideConfirmationText}
                browseFilePickerRef={browseFilePickerRef}
            />
            {!hideUploadButtons && onDoneClicked && (
                <div className="upload-files-button-container">
                    <DocumentUploadFormButtons
                        onUploadClicked={handleUploadClicked}
                        onResetClicked={resetForm}
                        onDoneClicked={onDoneClicked}
                        filesWereUploaded={filesWereUploaded}
                        filesAreStaged={uploadedFiles.length > 0}
                        isUploading={isUploading}
                    />
                </div>
            )}
        </div>
    )
})
export default observer(DocumentUploadForm)
